import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { CopyPaste } from "../misc/CopyPaste";
import { formatLocalizedDate } from "../misc/RelativeDate";
import type { LeadApiKey } from "../types";
import {
  generateLeadApiKey,
  getLeadApiKeyPrefix,
  hashLeadApiKey,
} from "./leadApiKey";

type LeadParamType = "text" | "number" | "date";

const LEAD_PARAMS: { name: string; type: LeadParamType; required?: boolean }[] =
  [
    { name: "email", type: "text", required: true },
    { name: "first_name", type: "text" },
    { name: "last_name", type: "text" },
    { name: "phone", type: "text" },
    { name: "company", type: "text" },
    { name: "event_type", type: "text" },
    { name: "event_date", type: "date" },
    { name: "guests", type: "number" },
    { name: "budget", type: "number" },
    { name: "message", type: "text" },
    { name: "source", type: "text" },
  ];

const EXAMPLE_BODY = {
  email: "julie.favre@exemple.ch",
  first_name: "Julie",
  last_name: "Favre",
  company: "Banque Lemanique",
  event_type: "Seminaire",
  event_date: "2026-12-04",
  guests: 120,
  budget: 15000,
  message: "Seminaire de deux jours avec repas",
};

/** Settings section of the lead push API: endpoint, API key and parameter reference. */
export const SettingsLeadApi = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [locale = "fr"] = useLocaleState();
  const [newKey, setNewKey] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/leads`;

  const { data: activeKeys = [], refetch } = useGetList<LeadApiKey>(
    "lead_api_keys",
    {
      filter: { "revoked_at@is": null },
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 10 },
    },
  );
  const activeKey = activeKeys[0];

  const { mutate: generateKey, isPending } = useMutation({
    mutationFn: async () => {
      const key = generateLeadApiKey();
      const now = new Date().toISOString();
      if (activeKeys.length > 0) {
        await dataProvider.updateMany("lead_api_keys", {
          ids: activeKeys.map((apiKey) => apiKey.id),
          data: { revoked_at: now },
        });
      }
      await dataProvider.create("lead_api_keys", {
        data: {
          key_hash: await hashLeadApiKey(key),
          key_prefix: getLeadApiKeyPrefix(key),
          created_at: now,
        },
      });
      return key;
    },
    onSuccess: (key) => {
      setNewKey(key);
      refetch();
    },
    onError: () => {
      notify("crm.settings.lead_api.generate_error", { type: "error" });
    },
  });

  const example = [
    `curl -X POST ${endpoint} \\`,
    `  -H "X-API-Key: ${newKey ?? "VOTRE_CLE_API"}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${JSON.stringify(EXAMPLE_BODY)}'`,
  ].join("\n");

  return (
    <Card id="lead-api">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.lead_api")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.lead_api.help")}
        </p>

        <div className="space-y-1">
          <h3 className="text-sm font-medium">
            {translate("crm.settings.lead_api.endpoint")}
          </h3>
          <CopyPaste value={endpoint} />
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">
            {translate("crm.settings.lead_api.key")}
          </h3>
          {activeKey ? (
            <p className="text-sm">
              {translate("crm.settings.lead_api.key_status", {
                prefix: activeKey.key_prefix,
                date: formatLocalizedDate(activeKey.created_at, locale),
              })}
              <br />
              <span className="text-muted-foreground">
                {activeKey.last_used_at
                  ? translate("crm.settings.lead_api.last_used", {
                      date: formatLocalizedDate(activeKey.last_used_at, locale),
                    })
                  : translate("crm.settings.lead_api.never_used")}
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.lead_api.no_key")}
            </p>
          )}
          {newKey && (
            <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1">
              <p className="text-sm font-medium">
                {translate("crm.settings.lead_api.new_key")}
              </p>
              <CopyPaste value={newKey} />
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => (activeKey ? setIsConfirmOpen(true) : generateKey())}
          >
            <KeyRound className="h-4 w-4 mr-1" />
            {translate(
              activeKey
                ? "crm.settings.lead_api.regenerate"
                : "crm.settings.lead_api.generate",
            )}
          </Button>
          <Confirm
            isOpen={isConfirmOpen}
            title="crm.settings.lead_api.regenerate_title"
            content="crm.settings.lead_api.regenerate_content"
            onConfirm={() => {
              setIsConfirmOpen(false);
              generateKey();
            }}
            onClose={() => setIsConfirmOpen(false)}
          />
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.lead_api.auth_help")}
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">
            {translate("crm.settings.lead_api.parameters")}
          </h3>
          <table className="w-full text-sm">
            <tbody>
              {LEAD_PARAMS.map((param) => (
                <tr key={param.name} className="border-t align-top">
                  <td className="py-1 pr-3 font-mono whitespace-nowrap">
                    {param.name}
                  </td>
                  <td className="py-1 pr-3 text-muted-foreground whitespace-nowrap">
                    {translate(`crm.settings.lead_api.types.${param.type}`)}
                    {param.required &&
                      ` · ${translate("crm.settings.lead_api.required")}`}
                  </td>
                  <td className="py-1">
                    {translate(`crm.settings.lead_api.params.${param.name}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-1">
          <h3 className="text-sm font-medium">
            {translate("crm.settings.lead_api.example")}
          </h3>
          <pre className="text-xs bg-muted rounded-md p-3 overflow-x-auto whitespace-pre">
            {example}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
};
